import PropTypes from 'prop-types';
import React, { memo, useMemo } from 'react';
import { Button, Typography } from 'antd';
import { useHistory } from 'react-router-dom';
import Card from 'components/Card/Card';
import { Launch } from '@carbon/icons-react';
import { PROVIDER_APPLICATION_PROGRAM_TYPES } from 'containers/Provider/SelectProgram/SelectProgram';
import { FormItemLabel } from 'components/Form';
import FormattedAddress from 'components/FormattedAddress/FormattedAddress';

const { Title, Paragraph, Text } = Typography;

function ApplicationSubmissionSuccess({ application }) {
  const history = useHistory();

  //For displaying the proper email
  const emailAddress = useMemo(() => {
    const programTypeId = application?.programType?.id;
    switch (programTypeId) {
      case PROVIDER_APPLICATION_PROGRAM_TYPES.AIR_FORCE_HCC.id:
        return 'afhcc@usa.childcareaware.org';
      case PROVIDER_APPLICATION_PROGRAM_TYPES.NAVY_HCC.id:
        return 'navyhcc@usa.childcareaware.org';
      default:
        return 'za-ProviderServices@usa.childcareaware.org';
    }
  }, [application]);

  const phoneExtension = useMemo(() => {
    const programTypeId = application?.programType?.id;
    switch (programTypeId) {
      case PROVIDER_APPLICATION_PROGRAM_TYPES.NAVY_HCC.id:
        return 'ext. 404';
      case PROVIDER_APPLICATION_PROGRAM_TYPES.AIR_FORCE_HCC.id:
        return 'ext. 406';
      default:
        return 'ext. 4, then 1';
    }
  }, [application]);

  const programName = Object.values(PROVIDER_APPLICATION_PROGRAM_TYPES).find(
    (program) => program.id === application?.programType.id,
  )?.name;

  return (
    <Typography>
      <Title level={1} className="mt-4">
        {programName ? `${programName} - ` : ''}Application submitted!
      </Title>

      <Card
        title={
          <Title level={3} className="mt-4">
            For {application?.careFacility?.displayName || application?.careFacility?.businessLegalName}
          </Title>
        }
      >
        <FormItemLabel title="Care Location:" />
        <FormattedAddress address={application?.careFacility?.address} />
      </Card>

      <Card
        title={
          <Title level={3} className="mt-4">
            What happens next
          </Title>
        }
        className="mt-6"
        data-testid="submissionSuccessCard"
      >
        <Paragraph>
          We will assign your application to a Provider Services Specialist. We process applications and documents in
          the order they are received.
        </Paragraph>
        <Paragraph>
          The military service branches determine eligibility requirements for fee assistance. Families and providers
          must meet all requirements before receiving approval for fee assistance.
        </Paragraph>
        <Paragraph>
          If you have any questions, you can:
          <ul>
            <li>
              Call <Text strong>1-800-424-2246, {phoneExtension},</Text> between 8:30 am and 7pm Eastern Time
            </li>
            <li>
              Email <Text strong>{emailAddress}</Text>
            </li>
          </ul>
        </Paragraph>
      </Card>

      <Card
        title={
          <Title level={3} className="mt-4">
            Give feedback
          </Title>
        }
        className="mt-6"
      >
        <Paragraph>Help us improve your application experience by completing a brief feedback survey.</Paragraph>

        <a href="https://forms.office.com/r/essGvV4VDj" target="_blank" rel="noreferrer">
          <Button type="primary" style={{ borderRadius: '2px' }}>
            Start Survey <Launch />
          </Button>
        </a>
      </Card>

      <div className="actions flex flex-col-reverse md:flex-row justify-end items-center mt-4">
        <Button onClick={() => history.push('/provider/locations-and-programs')} style={{ borderRadius: '2px' }}>
          Go to dashboard
        </Button>
      </div>
    </Typography>
  );
}

ApplicationSubmissionSuccess.propTypes = {
  isSubmitted: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default memo(ApplicationSubmissionSuccess);
